import Phaser from 'phaser';
import { drawAsgardSilhouette, drawAsgardSkyBackground } from './art/asgardBackdrop';
import {
  drawAvengersTowerSilhouette,
  drawAvengersTowerSkyBackground,
} from './art/avengersTowerBackdrop';
import { drawNycSilhouette, drawNycSkyBackground } from './art/nycSilhouette';
import {
  drawCosmicSilhouette,
  drawCosmicSkyBackground,
} from './art/cosmicBackdrop';
import {
  drawDarkDimensionSilhouette,
  drawDarkDimensionSkyBackground,
} from './art/darkDimensionBackdrop';
import {
  drawNightCitySilhouette,
  drawNightCitySkyBackground,
} from './art/nightCityBackdrop';
import {
  drawWakandaSilhouette,
  drawWakandaSkyBackground,
} from './art/wakandaBackdrop';
import {
  drawMutantIslandSilhouette,
  drawMutantIslandSkyBackground,
} from './art/mutantIslandBackdrop';
import {
  drawKnowhereSilhouette,
  drawKnowhereSkyBackground,
} from './art/knowhereBackdrop';
import {
  drawHellsKitchenSilhouette,
  drawHellsKitchenSkyBackground,
} from './art/hellsKitchenBackdrop';
import {
  drawAngelGroveSilhouette,
  drawAngelGroveSkyBackground,
} from './art/angelGroveBackdrop';
import {
  drawMoonPalaceSilhouette,
  drawMoonPalaceSkyBackground,
} from './art/moonPalaceBackdrop';
import {
  drawZordBattleSilhouette,
  drawZordBattleSkyBackground,
} from './art/zordBattleBackdrop';
import {
  drawGalactusSilhouette,
  drawGalactusSkyBackground,
} from './art/galactusBackdrop';
import { getCurrentMode } from './mode';
import { drawWorldTab, WorldTabState } from './art/worldTabs';
import {
  isLevelUnlocked,
  isWorldUnlocked,
  LevelConfig,
  WORLDS,
  WorldNumber,
  worldsForUniverse,
} from './levels';
import { loadSave, setLastViewedWorld, starsFor } from './save';

const NODE_R = 26;
const BOSS_R = 36;
const PATH_AMPLITUDE = 28;
const PATH_DASH = 8;
const PATH_GAP = 8;

const TAB_R = 26;
const TAB_GAP = 14;

let hasBootedThisSession = false;

const C_LOCKED = 0x2a3558;
const C_UNLOCKED = 0xffd24a;
const C_COMPLETED = 0x22c55e;
const C_BOSS = 0x7f1d1d;
const C_BOSS_EDGE = 0xef4444;
const C_PATH = 0x4b5563;
const C_STROKE = 0x0a0f1f;

export class LevelMapScene extends Phaser.Scene {
  private bgGraphics!: Phaser.GameObjects.Graphics;
  private silhouetteGraphics!: Phaser.GameObjects.Graphics;
  private pathGraphics!: Phaser.GameObjects.Graphics;
  private nodes: Phaser.GameObjects.Container[] = [];
  private worldTabContainers: Phaser.GameObjects.Container[] = [];
  private heroesButton?: Phaser.GameObjects.Container;
  private powerUpsButton?: Phaser.GameObjects.Container;

  private currentWorld: WorldNumber = 1;

  constructor() {
    super('LevelMap');
  }

  init(data: { world?: WorldNumber } = {}) {
    const save = loadSave();
    const visible = worldsForUniverse(getCurrentMode());
    const fallback: WorldNumber = visible[0] ?? 1;

    let resolved: WorldNumber;
    if (data.world !== undefined) {
      resolved = data.world;
    } else if (!hasBootedThisSession) {
      resolved = fallback;
    } else {
      const stored = save.lastViewedWorld as WorldNumber | undefined;
      resolved = stored !== undefined && visible.includes(stored) ? stored : fallback;
    }

    this.currentWorld = visible.includes(resolved) ? resolved : fallback;
    hasBootedThisSession = true;
    setLastViewedWorld(this.currentWorld);
  }

  create() {
    this.bgGraphics = this.add.graphics();
    this.silhouetteGraphics = this.add.graphics();
    this.pathGraphics = this.add.graphics();

    this.layout();

    this.scale.on('resize', () => this.layout());
  }

  private layout() {
    const w = this.scale.width;
    const h = this.scale.height;

    this.drawBackdrop(w, h);

    this.nodes.forEach((n) => n.destroy());
    this.nodes = [];
    this.worldTabContainers.forEach((t) => t.destroy());
    this.worldTabContainers = [];
    this.heroesButton?.destroy();

    const worldLevels = WORLDS[this.currentWorld];
    const save = loadSave();

    if (worldLevels.length > 0) {
      const positions = this.nodePositions(w, h, worldLevels.length);
      this.drawPath(positions);
      worldLevels.forEach((level, i) => {
        const { x, y } = positions[i];
        const unlocked = isLevelUnlocked(level.id, save.starsByLevelId);
        const stars = starsFor(save, level.id);
        const node = this.makeNode(level, x, y, unlocked, stars);
        this.nodes.push(node);
      });
    } else {
      this.pathGraphics.clear();
    }

    this.drawWorldTabs(w, save.starsByLevelId);
    this.drawHeroesButton();
    this.drawPowerUpsButton();
    this.drawHomeButton();
  }

  private drawBackdrop(w: number, h: number) {
    this.bgGraphics.clear();
    this.silhouetteGraphics.clear();
    switch (this.currentWorld) {
      case 1:
        drawNycSkyBackground(this.bgGraphics, w, h);
        drawNycSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 2:
        drawAsgardSkyBackground(this.bgGraphics, w, h);
        drawAsgardSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 3:
        drawAvengersTowerSkyBackground(this.bgGraphics, w, h);
        drawAvengersTowerSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 4:
        drawWakandaSkyBackground(this.bgGraphics, w, h);
        drawWakandaSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 5:
        drawCosmicSkyBackground(this.bgGraphics, w, h);
        drawCosmicSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 6:
        drawDarkDimensionSkyBackground(this.bgGraphics, w, h);
        drawDarkDimensionSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 7:
        drawNightCitySkyBackground(this.bgGraphics, w, h);
        drawNightCitySilhouette(this.silhouetteGraphics, w, h);
        return;
      case 8:
        drawMutantIslandSkyBackground(this.bgGraphics, w, h);
        drawMutantIslandSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 9:
        drawKnowhereSkyBackground(this.bgGraphics, w, h);
        drawKnowhereSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 10:
        drawAngelGroveSkyBackground(this.bgGraphics, w, h);
        drawAngelGroveSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 11:
        drawMoonPalaceSkyBackground(this.bgGraphics, w, h);
        drawMoonPalaceSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 12:
        drawZordBattleSkyBackground(this.bgGraphics, w, h);
        drawZordBattleSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 13:
        drawHellsKitchenSkyBackground(this.bgGraphics, w, h);
        drawHellsKitchenSilhouette(this.silhouetteGraphics, w, h);
        return;
      case 14:
        drawGalactusSkyBackground(this.bgGraphics, w, h);
        drawGalactusSilhouette(this.silhouetteGraphics, w, h);
        return;
      default:
        this.bgGraphics.clear();
        this.bgGraphics.fillStyle(0x0a0f1f, 1);
        this.bgGraphics.fillRect(0, 0, w, h);
        this.silhouetteGraphics.clear();
        return;
    }
  }

  private drawWorldTabs(w: number, starsByLevelId: Record<string, number>) {
    const visible = worldsForUniverse(getCurrentMode());
    const totalW = visible.length * (TAB_R * 2 + TAB_GAP) - TAB_GAP;
    const startX = Math.floor((w - totalW) / 2 + TAB_R);
    const y = 40;

    visible.forEach((world, i) => {
      const cx = startX + i * (TAB_R * 2 + TAB_GAP);
      const unlocked = isWorldUnlocked(world, starsByLevelId) && WORLDS[world].length > 0;
      const state: WorldTabState =
        world === this.currentWorld ? 'current' : unlocked ? 'unlocked' : 'locked';
      const container = this.add.container(cx, y);
      const g = this.add.graphics();
      drawWorldTab(g, world, TAB_R, state);
      container.add(g);
      if (state !== 'locked') {
        const hit = this.add
          .zone(0, 0, TAB_R * 2.4, TAB_R * 2.4)
          .setInteractive({ useHandCursor: true });
        hit.on('pointerup', () => {
          if (world !== this.currentWorld) {
            this.currentWorld = world;
            setLastViewedWorld(world);
            this.layout();
          }
        });
        container.add(hit);
      }
      this.worldTabContainers.push(container);
    });
  }

  private drawHeroesButton() {
    const w = this.scale.width;
    const cx = w - 36;
    const cy = 36;
    const container = this.add.container(cx, cy);

    const g = this.add.graphics();
    g.fillStyle(0x121a32, 1);
    g.lineStyle(3, 0xffd24a, 1);
    g.fillCircle(0, 0, 24);
    g.strokeCircle(0, 0, 24);
    g.fillStyle(0xffd24a, 1);
    g.fillCircle(0, -5, 6);
    g.fillRoundedRect(-10, 2, 20, 12, 3);
    container.add(g);

    const hit = this.add.zone(0, 0, 60, 60).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.scene.start('Roster'));
    container.add(hit);
    this.heroesButton = container;
  }

  private drawPowerUpsButton() {
    const w = this.scale.width;
    const cx = w - 36;
    const cy = 92;
    const container = this.add.container(cx, cy);

    const g = this.add.graphics();
    g.fillStyle(0x121a32, 1);
    g.lineStyle(3, 0xa855f7, 1);
    g.fillCircle(0, 0, 24);
    g.strokeCircle(0, 0, 24);
    const gemColors = [0x9333ea, 0xfacc15, 0xef4444, 0x3b82f6, 0x22c55e, 0xf97316];
    for (let i = 0; i < gemColors.length; i++) {
      const a = (i / gemColors.length) * Math.PI * 2 - Math.PI / 2;
      g.fillStyle(gemColors[i], 1);
      g.fillCircle(Math.cos(a) * 10, Math.sin(a) * 10, 4);
    }
    container.add(g);

    this.powerUpsButton?.destroy();
    const hit = this.add.zone(0, 0, 60, 60).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.scene.start('PowerUps'));
    container.add(hit);
    this.powerUpsButton = container;
  }

  private drawHomeButton() {
    const w = this.scale.width;
    const cx = w - 36;
    const cy = 148;
    const container = this.add.container(cx, cy);

    const g = this.add.graphics();
    g.fillStyle(0x121a32, 1);
    g.lineStyle(3, 0x22c55e, 1);
    g.fillCircle(0, 0, 24);
    g.strokeCircle(0, 0, 24);
    g.fillStyle(0x22c55e, 1);
    g.beginPath();
    g.moveTo(-10, 2);
    g.lineTo(0, -10);
    g.lineTo(10, 2);
    g.closePath();
    g.fillPath();
    g.fillRect(-7, 2, 14, 10);
    g.fillStyle(0x0a0f1f, 1);
    g.fillRect(-3, 6, 6, 6);
    container.add(g);

    const hit = this.add.zone(0, 0, 60, 60).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.scene.start('Menu'));
    container.add(hit);
  }

  private nodePositions(w: number, h: number, count: number) {
    const marginX = Math.max(60, Math.floor(w * 0.08));
    const centerY = Math.floor(h * 0.5);
    const stepX = (w - marginX * 2) / (count - 1);
    return Array.from({ length: count }, (_, i) => {
      const x = marginX + i * stepX;
      const phase = (i / (count - 1)) * Math.PI * 2;
      const y = centerY + Math.sin(phase) * PATH_AMPLITUDE;
      return { x: Math.floor(x), y: Math.floor(y) };
    });
  }

  private drawPath(positions: { x: number; y: number }[]) {
    const g = this.pathGraphics;
    g.clear();
    g.lineStyle(3, C_PATH, 0.9);
    for (let i = 0; i < positions.length - 1; i++) {
      this.drawDashedSegment(g, positions[i], positions[i + 1]);
    }
  }

  private drawDashedSegment(
    g: Phaser.GameObjects.Graphics,
    a: { x: number; y: number },
    b: { x: number; y: number },
  ) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    const steps = Math.floor(len / (PATH_DASH + PATH_GAP));
    const ux = dx / len;
    const uy = dy / len;
    let cursor = 0;
    for (let i = 0; i < steps; i++) {
      const sx = a.x + ux * cursor;
      const sy = a.y + uy * cursor;
      const ex = sx + ux * PATH_DASH;
      const ey = sy + uy * PATH_DASH;
      g.beginPath();
      g.moveTo(sx, sy);
      g.lineTo(ex, ey);
      g.strokePath();
      cursor += PATH_DASH + PATH_GAP;
    }
  }

  private makeNode(
    level: LevelConfig,
    x: number,
    y: number,
    unlocked: boolean,
    stars: 0 | 1 | 2 | 3,
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const g = this.add.graphics();
    const r = level.isBoss ? BOSS_R : NODE_R;
    const completed = stars > 0;

    if (level.isBoss) {
      g.fillStyle(C_BOSS, 1);
      g.lineStyle(3, C_BOSS_EDGE, unlocked ? 1 : 0.4);
      g.fillCircle(0, 0, r);
      g.strokeCircle(0, 0, r);
      drawSkullMark(g, 0, 0, r * 0.5, unlocked ? 0xffffff : 0x64748b);
      if (!unlocked) drawPadlock(g, 0, 0, r * 0.4, 0x64748b);
    } else if (completed) {
      g.fillStyle(C_COMPLETED, 1);
      g.lineStyle(2, C_STROKE, 1);
      g.fillCircle(0, 0, r);
      g.strokeCircle(0, 0, r);
      g.fillStyle(0xffffff, 1);
      g.fillCircle(0, 0, r * 0.35);
    } else if (unlocked) {
      g.fillStyle(C_UNLOCKED, 1);
      g.lineStyle(2, C_STROKE, 1);
      g.fillCircle(0, 0, r);
      g.strokeCircle(0, 0, r);
    } else {
      g.fillStyle(C_LOCKED, 1);
      g.lineStyle(2, C_STROKE, 1);
      g.fillCircle(0, 0, r);
      g.strokeCircle(0, 0, r);
      drawPadlock(g, 0, 0, r * 0.5, 0x8ca0c4);
    }
    container.add(g);

    if (completed) {
      const starsG = this.add.graphics();
      drawStarsRow(starsG, 0, -r - 14, stars, 8);
      container.add(starsG);
    }

    if (unlocked) {
      const hit = this.add.zone(0, 0, r * 2.4, r * 2.4).setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => this.onNodeTap(level));
      container.add(hit);

      this.tweens.add({
        targets: g,
        scale: 1.07,
        duration: 900,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    const orderBadge = this.add
      .text(0, 0, level.isBoss ? '' : String(level.order), {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: level.isBoss ? '0px' : '20px',
        color: '#0a0f1f',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    container.add(orderBadge);

    return container;
  }

  private onNodeTap(level: LevelConfig) {
    this.scene.start('TeamPicker', { levelId: level.id });
  }
}

function drawPadlock(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  size: number,
  color: number,
) {
  const bodyW = size;
  const bodyH = size * 0.75;
  const shackleR = size * 0.35;
  g.fillStyle(color, 1);
  g.fillRoundedRect(cx - bodyW / 2, cy - bodyH / 2 + shackleR * 0.3, bodyW, bodyH, 2);
  g.lineStyle(Math.max(2, Math.floor(size * 0.15)), color, 1);
  g.beginPath();
  g.arc(cx, cy - bodyH / 2 + shackleR * 0.1, shackleR, Math.PI, 0, false);
  g.strokePath();
}

function drawSkullMark(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  size: number,
  color: number,
) {
  g.fillStyle(color, 1);
  g.fillCircle(cx, cy - size * 0.05, size * 0.55);
  g.fillStyle(0x0a0f1f, 1);
  g.fillCircle(cx - size * 0.22, cy - size * 0.05, size * 0.12);
  g.fillCircle(cx + size * 0.22, cy - size * 0.05, size * 0.12);
  g.fillStyle(color, 1);
  g.fillRect(cx - size * 0.3, cy + size * 0.3, size * 0.6, size * 0.18);
  g.fillStyle(0x0a0f1f, 1);
  for (let i = -1; i <= 1; i++) {
    g.fillRect(cx + i * size * 0.18 - size * 0.04, cy + size * 0.3, size * 0.08, size * 0.18);
  }
}

function drawStarsRow(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  count: number,
  starSize: number,
) {
  const total = 3;
  const spacing = starSize * 2.2;
  const startX = cx - ((total - 1) * spacing) / 2;
  for (let i = 0; i < total; i++) {
    const filled = i < count;
    drawStar(g, startX + i * spacing, cy, starSize, starSize * 0.42, 5, filled ? 0xfbbf24 : 0x2a3558);
  }
}

function drawStar(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  points: number,
  fill: number,
) {
  const pts: { x: number; y: number }[] = [];
  const step = Math.PI / points;
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const a = -Math.PI / 2 + i * step;
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  g.fillStyle(fill, 1);
  g.lineStyle(1, 0x0a0f1f, 1);
  g.beginPath();
  g.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
  g.closePath();
  g.fillPath();
  g.strokePath();
}
